/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useState } from 'react'
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  Typography
} from '@mui/material'
import { Breadcrumb } from '../../common/components'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ProjectApi, SystemApi } from '../../common/services'
import { t } from 'i18next'
import { Canvas } from './components'
import KeyTranslation from '../../common/KeyTranslation.json'

export default function OverviewSystem (): JSX.Element {
  const [isLoading, setIsLoading] = useState(true)
  const id = useParams()
  const projectId = Number(id.id)
  const systemId = Number(id.systemId)
  const [isDirty, setIsDirty] = useState(false)
  const [saveFn, setSaveFn] = useState<(() => Promise<void>) | null>(null)
  const [showConfirm, setShowConfirm] = useState(false)
  const [pendingPath, setPendingPath] = useState<string | null>(null)
  const navigate = useNavigate()
  const {
    data: systemData,
    refetch: systemRefetch,
    isFetched
  } = useQuery(
    ['/api/v2/System/{id}', systemId],
    async () => {
      const { data } = await new SystemApi().apiV2SystemIdGet(systemId)
      setIsLoading(false)
      return data
    },
    { enabled: false }
  )
  const {
    data: projectData,
    refetch: projectRefetch,
    isFetched: isFetchedProject
  } = useQuery(
    ['/api/v2/Project/GetProjectById', projectId],
    async () => {
      const { data } = await new ProjectApi().apiV2ProjectGetProjectByIdGet(
        projectId
      )
      return data
    },
    { enabled: false }
  )
  React.useEffect(() => {
    void projectRefetch()
    void systemRefetch()
  }, [])
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    to: string
  ) => {
    if (isDirty) {
      e.preventDefault()
      setPendingPath(to)
      setShowConfirm(true)
    }
  }
  return (
    <Box>
      {isLoading && (
        <div className="loading">
          <CircularProgress />
        </div>
      )}
      <Box>
        <Grid
          container
          spacing={2}
          mb={4}
          alignItems="center"
          sx={{ marginLeft: 0, marginTop: 0 }}
        >
          <Grid item xs={4}>
            <Typography variant="h1" mb={4}>
              {projectData?.data?.projectName}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Box>
              <Typography variant="caption">
                {t(KeyTranslation.Common_CustomerName)}:{' '}
              </Typography>
              <Typography component={'span'}>
                {projectData?.data?.customerName}
              </Typography>
            </Box>
            <Box>
              <Typography component={'span'} variant="caption">
                {t(KeyTranslation.Header_ProjectName)}:{' '}
              </Typography>
              <Typography component={'span'}>
                {projectData?.data?.projectName}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box>
              <Typography variant="caption">
                {t(KeyTranslation.Common_ProjectRef)}:{' '}
              </Typography>
              <Typography component={'span'}>
                {projectData?.data?.reference}
              </Typography>
            </Box>
            <Box>
              <Typography component={'span'} variant="caption">
                {t(KeyTranslation.Common_System_name)}:{' '}
              </Typography>
              <Typography component={'span'}>
                {systemData?.data?.name}
              </Typography>
              <Box>
                <Link
                  to={`/projects/${projectId ?? 0}/system-overview/${systemId}`}
                  onClick={(e) => {
                    handleLinkClick(
                      e,
                      `/projects/${projectId ?? 0}/system-overview/${systemId}`
                    )
                  }}
                >
                  {t(KeyTranslation.Review_Edit_System)}
                </Link>
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Breadcrumbs aria-label="breadcrumb">
          <Breadcrumb
            component="div"
            label={
              <Link
                to={'/'}
                onClick={(e) => {
                  handleLinkClick(e, '/')
                }}
              >
                {t(KeyTranslation.Common_ProjectsDashboard)}
              </Link>
            }
          />
          <Breadcrumb
            component="div"
            label={
              <Link
                to={`/projects/${projectId}`}
                onClick={(e) => {
                  handleLinkClick(e, `/projects/${projectId}`)
                }}
              >
                {t(KeyTranslation.Common_ProjectOverview)}
              </Link>
            }
          />
          <Breadcrumb
            component="div"
            label={t(KeyTranslation.System_SystemLayoutEditor)}
          />
        </Breadcrumbs>
        {isFetched &&
          isFetchedProject &&
          systemData !== undefined &&
          systemData.data !== undefined &&
          projectData?.data !== undefined && (
            <Canvas
              systemData={systemData.data}
              projectData={projectData.data}
              setIsLoading={setIsLoading}
              onDirtyChange={setIsDirty}
              onSave={(fn) => {
                setSaveFn(() => fn)
              }}
            />
        )}
        <Dialog
          open={showConfirm}
          onClose={() => {
            setShowConfirm(false)
          }}
        >
          <DialogTitle>
            {`Want to save your changes to ${systemData?.data?.name ?? ''}?`}
          </DialogTitle>

          <DialogActions>
            <Button
              onClick={() => {
                setShowConfirm(false)
              }}
            >
              {t(KeyTranslation.Common_Cancel)}
            </Button>

            <Button
              onClick={() => {
                setShowConfirm(false)
                if (pendingPath != null) navigate(pendingPath)
              }}
            >
              {t("Don't Save")}
            </Button>

            <Button
              variant="contained"
              onClick={async () => {
                if (saveFn != null) await saveFn()
                setShowConfirm(false)
                if (pendingPath != null) navigate(pendingPath)
              }}
            >
              {t(KeyTranslation.Common_Save)}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  )
}
